import React, { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import {
  ACCORDION_GROUPS,
  ACCORDION_TOGGLE_LABEL,
  DUTIES_HEADLINE,
  DUTIES_PHASES,
  DUTIES_SCAN_PILLS,
  DUTIES_STAMP,
  DUTIES_SUBHEAD,
  HONEST_LIMITS,
} from "./dutiesContent";

const EmployeeDutiesSection = () => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 overflow-hidden"
      data-testid="employee-duties-section"
      ref={ref}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(255,255,255,0.025) 79px, rgba(255,255,255,0.025) 80px)",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
          <div className="lg:w-48 flex-shrink-0">
            <p className="badge-mono text-[10px] font-bold tracking-[0.3em] text-orange-400/90 uppercase mb-4">
              {DUTIES_STAMP}
            </p>
            <p className="hidden lg:block text-[10px] font-mono text-gray-600 leading-relaxed tracking-widest uppercase [writing-mode:vertical-rl] rotate-180 h-32">
              Shift log · 4 phases
            </p>
          </div>

          <div className="flex-1 min-w-0">
            <div
              className="flex flex-wrap gap-2 sm:gap-3 mb-5"
              data-testid="duties-scan-pills"
            >
              {DUTIES_SCAN_PILLS.map((pill) => (
                <div
                  key={pill.label}
                  className="flex items-baseline gap-2 px-3 py-2 rounded-lg border border-orange-500/25 bg-orange-500/5"
                >
                  <span className="font-mono font-bold text-lg sm:text-xl text-orange-400 leading-none">
                    {pill.value}
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-mono text-gray-500 tracking-wide">
                    {pill.label}
                  </span>
                </div>
              ))}
            </div>

            <h2
              className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-3"
              data-testid="duties-headline"
            >
              {DUTIES_HEADLINE.lead}{" "}
              <span className="text-orange-400">{DUTIES_HEADLINE.accent}</span>
            </h2>

            <p className="font-body text-sm sm:text-base text-[#9CA3AF] mb-8 max-w-2xl leading-relaxed">
              {DUTIES_SUBHEAD}
            </p>

            <div className="space-y-3 sm:space-y-4" data-testid="duties-phase-board">
              {DUTIES_PHASES.map((phase, phaseIndex) => {
                const isHero = phase.highlight;

                return (
                  <motion.div
                    key={phase.id}
                    initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                    animate={
                      reduceMotion || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }
                    }
                    transition={{ duration: 0.35, delay: phaseIndex * 0.08 }}
                    className={`relative rounded-xl border overflow-hidden ${
                      isHero
                        ? "border-orange-500/35 bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-transparent"
                        : "border-white/[0.07] bg-white/[0.015]"
                    }`}
                    data-testid={`employee-duty-phase-${phaseIndex}`}
                  >
                    {isHero && (
                      <span className="absolute top-3 right-3 badge-mono text-[8px] text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full">
                        KEY PHASE
                      </span>
                    )}

                    <div className="flex flex-col sm:flex-row">
                      <div
                        className={`flex sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:gap-1 px-4 sm:px-5 py-3 sm:py-5 sm:w-36 flex-shrink-0 border-b sm:border-b-0 sm:border-r ${
                          isHero
                            ? "border-orange-500/20 bg-orange-500/[0.06]"
                            : "border-white/[0.06] bg-white/[0.02]"
                        }`}
                      >
                        <span className="font-mono text-2xl sm:text-3xl font-bold text-gray-600 leading-none">
                          {phase.phase}
                        </span>
                        <div>
                          <p
                            className={`font-mono text-xs sm:text-sm font-bold tracking-wider ${
                              isHero ? "text-orange-400" : "text-teal-400/90"
                            }`}
                          >
                            {phase.label}
                          </p>
                          <p className="text-[10px] text-gray-500 font-body mt-0.5 hidden sm:block">
                            {phase.tagline}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06] flex-1">
                        {phase.duties.map((duty, dutyIndex) => (
                          <div
                            key={duty.title}
                            className="px-4 sm:px-5 py-4"
                            data-testid={`employee-duty-${phaseIndex * 2 + dutyIndex}`}
                          >
                            <p className="font-heading font-semibold text-xs sm:text-sm text-[#F3F4F6] mb-1">
                              {duty.title}
                            </p>
                            <p className="text-[10px] sm:text-[11px] text-gray-500 font-body leading-relaxed">
                              {duty.detail}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div
              className="mt-8 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              data-testid="employee-duties-honest-limits"
            >
              <span className="badge-mono text-[9px] text-gray-500 tracking-widest flex-shrink-0">
                HONEST LIMITS
              </span>
              <p className="text-[11px] sm:text-xs text-gray-400 font-body leading-relaxed">
                <span className="text-gray-300">{HONEST_LIMITS.lead}</span>{" "}
                {HONEST_LIMITS.close}
              </p>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => setAccordionOpen((open) => !open)}
                className="group inline-flex items-center gap-2 border border-white/[0.08] text-[#9CA3AF] font-mono text-xs py-2.5 px-4 rounded-lg hover:border-orange-500/30 hover:text-[#F3F4F6] transition-all duration-300"
                data-testid="employee-duties-accordion-toggle"
                aria-expanded={accordionOpen}
              >
                {ACCORDION_TOGGLE_LABEL}
                {accordionOpen ? (
                  <ChevronUp className="w-3.5 h-3.5 text-orange-400" aria-hidden="true" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-orange-400" aria-hidden="true" />
                )}
              </button>

              {accordionOpen && (
                <div
                  className="mt-4 rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.06]"
                  data-testid="employee-duties-accordion-panel"
                >
                  {ACCORDION_GROUPS.map((group, groupIndex) => (
                    <div
                      key={groupIndex}
                      className="px-4 sm:px-5 py-4 bg-white/[0.01]"
                      data-testid={`employee-duties-accordion-group-${groupIndex}`}
                    >
                      <h4 className="font-mono text-[10px] font-bold text-orange-400/80 tracking-wider mb-3">
                        {group.title.toUpperCase()}
                      </h4>
                      <ul className="space-y-2">
                        {group.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-2">
                            <CheckCircle2
                              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-teal-400/70"
                              aria-hidden="true"
                            />
                            <span className="font-body text-[11px] sm:text-xs text-gray-500 leading-relaxed">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployeeDutiesSection;
